import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import Highlight from '../Highlight';
import { searchTerms, matchesAllTerms } from '../../utils/search';

const MENU_MAX_HEIGHT = 280;

/**
 * Selettore a tendina con menu personalizzato (al posto del <select> nativo,
 * il cui elenco non è stilabile). `options` è un elenco di { value, label };
 * `onChange` riceve il `value` originale dell'opzione scelta. Il menu è nel
 * body, quindi non viene tagliato dai pannelli che scorrono. Con `searchable` il
 * menu ha in cima un campo per filtrare le voci (utile con elenchi lunghi).
 */
function Dropdown({ value, options, onChange, ariaLabel, className = '', style, id, disabled = false, pill = false, swatch = false, searchable = false }) {
  const { t } = useLanguage();
  const buttonRef = useRef(null);
  const searchRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [place, setPlace] = useState(null);
  const [query, setQuery] = useState('');

  // Le voci mostrate nel menu: tutte, o quelle che contengono il testo cercato.
  // Più parole separate da spazio: devono comparire tutte nella voce (AND).
  const terms = searchable ? searchTerms(query) : [];
  const visible = terms.length > 0 ? options.filter((o) => matchesAllTerms(o.label, terms)) : options;

  const currentIndex = options.findIndex((o) => String(o.value) === String(value));
  const selected = currentIndex >= 0 ? options[currentIndex] : null;
  const selectedIndex = visible.findIndex((o) => String(o.value) === String(value));

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);
  const buttonStyle = (pill || swatch) && selected?.color ? { '--pill': selected.color, ...style } : style;

  const openMenu = () => {
    if (disabled || options.length === 0) return;
    setActive(Math.max(0, currentIndex));
    setOpen(true);
  };

  const choose = (option) => {
    close();
    if (String(option.value) !== String(value)) onChange(option.value);
    buttonRef.current?.focus();
  };

  // Posizione sotto il pulsante (o sopra se sotto non c'è spazio), dentro lo schermo.
  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const height = Math.min(MENU_MAX_HEIGHT, options.length * 34 + 10 + (searchable ? 44 : 0));
    const below = window.innerHeight - rect.bottom - 8;
    const above = rect.top - 8;
    const up = below < height && above > below;
    const maxHeight = Math.max(120, Math.min(MENU_MAX_HEIGHT, up ? above : below));
    const width = Math.max(rect.width, 160);
    setPlace({
      left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
      top: up ? undefined : rect.bottom + 4,
      bottom: up ? window.innerHeight - rect.top + 4 : undefined,
      minWidth: width,
      maxHeight,
    });
  }, [open, options.length, searchable]);

  // Con la ricerca il cursore va subito nel campo, così si può scrivere senza cliccare.
  useEffect(() => {
    if (open && searchable && place) searchRef.current?.focus({ preventScroll: true });
  }, [open, searchable, place]);

  // Il menu segue il pulsante solo finché la pagina sta ferma: se la pagina scorre, si chiude.
  useEffect(() => {
    if (!open) return undefined;
    const onScroll = (e) => { if (!menuRef.current?.contains(e.target)) close(); };
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, close]);

  // La voce attiva resta visibile durante la navigazione da tastiera.
  useEffect(() => {
    if (!open || !place) return;
    menuRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [open, place, active]);

  const onKeyDown = (e) => {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) { e.preventDefault(); openMenu(); }
      return;
    }
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(visible.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Home' && !searchable) { e.preventDefault(); setActive(0); }
    else if (e.key === 'End' && !searchable) { e.preventDefault(); setActive(visible.length - 1); }
    else if (e.key === 'Enter' || (e.key === ' ' && !searchable)) { e.preventDefault(); if (visible[active]) choose(visible[active]); }
    else if (e.key === 'Tab') close();
  };

  return (
    <>
      <button
        type="button"
        ref={buttonRef}
        id={id}
        style={buttonStyle}
        className={`dd-btn ${className}${pill ? ' dd-pill' : ''}${swatch ? ' dd-swatch' : ''}${open ? ' dd-open' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={onKeyDown}
      >
        {swatch && <span className="dd-dot" aria-hidden="true" />}
        <span className="dd-value">{selected ? selected.label : '—'}</span>
        <svg className="dd-chevron" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && createPortal(
        <>
          <div className="dd-backdrop" onClick={close} />
          <div
            ref={menuRef}
            className="dd-menu"
            role="listbox"
            aria-label={ariaLabel}
            style={place ? { ...place } : { visibility: 'hidden' }}
          >
            {searchable && (
              <div className="dd-search">
                <input
                  ref={searchRef}
                  type="search"
                  value={query}
                  placeholder={t('common.searchPlaceholder')}
                  aria-label={t('common.searchPlaceholder')}
                  onChange={(e) => { setQuery(e.target.value); setActive(0); }}
                  onKeyDown={onKeyDown}
                />
                {query !== '' && (
                  <button
                    type="button"
                    className="dd-search-clear"
                    title={t('common.clearSearch')}
                    aria-label={t('common.clearSearch')}
                    onClick={() => { setQuery(''); setActive(0); searchRef.current?.focus(); }}
                  >
                    ×
                  </button>
                )}
              </div>
            )}
            {visible.length === 0 && <div className="dd-empty">{t('common.noMatches')}</div>}
            {visible.map((option, index) => {
              const isSelected = index === selectedIndex;
              return (
                <div
                  key={String(option.value)}
                  role="option"
                  aria-selected={isSelected}
                  data-active={index === active}
                  className={'dd-option' + (index === active ? ' dd-option-active' : '') + (isSelected ? ' dd-option-selected' : '')}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => choose(option)}
                >
                  {option.color && <span className="dd-dot" style={{ '--pill': option.color }} aria-hidden="true" />}
                  <span className="dd-option-label"><Highlight text={option.label} terms={terms} /></span>
                  {isSelected && (
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
              );
            })}
          </div>
        </>,
        document.body,
      )}
    </>
  );
}

export default Dropdown;
